import { apiClient } from './client';
import type {
  Order,
  OrderFilters,
  PaginationParams,
  PaginatedResponse,
  FSMTransition,
  SunoBalanceResponse,
} from '../types';

export const ordersApi = {
  // Get paginated order list with filters
  getOrders: async (
    filters: OrderFilters,
    pagination: PaginationParams
  ): Promise<PaginatedResponse<Order>> => {
    const params = {
      ...pagination,
      ...filters,
      status: filters.status?.join(','),
      channel: filters.channel?.join(',')
    };

    const response = await apiClient.get<PaginatedResponse<Order>>(
      '/admin/orders',
      { params }
    );
    return response.data;
  },

  // Get single order details
  getOrder: async (orderId: string): Promise<Order> => {
    const response = await apiClient.get<Order>(
      `/admin/orders/${orderId}`
    );
    return response.data;
  },

  // Get FSM transition history for order
  getOrderHistory: async (orderId: string): Promise<FSMTransition[]> => {
    const response = await apiClient.get<FSMTransition[]>(
      `/admin/orders/${orderId}/history`
    );
    return response.data;
  },

  // FSM Actions
  approveOrder: async (orderId: string): Promise<Order> => {
    const response = await apiClient.post<Order>(
      `/admin/orders/${orderId}/approve`
    );
    return response.data;
  },

  rejectOrder: async (orderId: string, reason: string): Promise<Order> => {
    const response = await apiClient.post<Order>(
      `/admin/orders/${orderId}/reject`,
      { reason }
    );
    return response.data;
  },

  regenerateLyrics: async (orderId: string): Promise<Order> => {
    const response = await apiClient.post<Order>(
      `/admin/orders/${orderId}/regenerate-lyrics`
    );
    return response.data;
  },

  retryGeneration: async (orderId: string): Promise<Order> => {
    const response = await apiClient.post<Order>(
      `/admin/orders/${orderId}/retry`
    );
    return response.data;
  },

  cancelOrder: async (orderId: string): Promise<Order> => {
    const response = await apiClient.post<Order>(
      `/admin/orders/${orderId}/cancel`
    );
    return response.data;
  },

  pauseOrder: async (orderId: string): Promise<Order> => {
    const response = await apiClient.post<Order>(
      `/admin/orders/${orderId}/pause`
    );
    return response.data;
  },

  resumeOrder: async (orderId: string): Promise<Order> => {
    const response = await apiClient.post<Order>(
      `/admin/orders/${orderId}/resume`
    );
    return response.data;
  },

  confirmPayment: async (orderId: string): Promise<Order> => {
    const response = await apiClient.post<Order>(
      `/admin/orders/${orderId}/confirm-payment`
    );
    return response.data;
  },

  // Manual state override (dangerous - bypasses FSM, no callbacks)
  forceStatus: async (orderId: string, newStatus: string): Promise<Order> => {
    const response = await apiClient.post<Order>(
      `/admin/orders/${orderId}/force-status`,
      { new_status: newStatus }
    );
    return response.data;
  },

  // Force transition WITH FSM callbacks (starts Celery tasks for target state)
  forceTransition: async (orderId: string, newStatus: string): Promise<Order> => {
    const response = await apiClient.post<Order>(
      `/admin/orders/${orderId}/force-transition`,
      { new_status: newStatus }
    );
    return response.data;
  },

  // Resend song to client (messenger + email)
  resendSong: async (orderId: string): Promise<Order> => {
    const response = await apiClient.post<Order>(
      `/admin/orders/${orderId}/resend-song`
    );
    return response.data;
  },

  // Check Suno API key credit balance
  getSunoBalance: async (): Promise<SunoBalanceResponse> => {
    const response = await apiClient.get<SunoBalanceResponse>('/admin/suno/balance');
    return response.data;
  },
};
